// availabilityUtils.ts
import { SLOT_PRESETS, SlotType, TimeSlot } from "./types";

const timeToMinutes = (time: string) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

// A slot conflicts if it overlaps another slot: start < other.end AND end > other.start
export const findConflict = (
  slots: Pick<TimeSlot, "id" | "start" | "end">[],
) => {
  const normalized = slots
    .filter((s) => s.start && s.end)
    .map((s) => ({
      ...s,
      sMin: timeToMinutes(s.start),
      eMin: timeToMinutes(s.end),
    }))
    .sort((a, b) => a.sMin - b.sMin);

  for (let i = 0; i < normalized.length; i++) {
    const cur = normalized[i];
    if (cur.eMin <= cur.sMin) {
      return { type: "range", message: "End time must be after start time." };
    }
    for (let j = i + 1; j < normalized.length; j++) {
      const next = normalized[j];
      const overlap = cur.sMin < next.eMin && cur.eMin > next.sMin;
      if (overlap) {
        return {
          type: "overlap",
          message: "Time slots are overlapping. Please adjust the timings.",
        };
      }
      // because sorted by start, if next starts after current ends, no need check further
      if (next.sMin >= cur.eMin) break;
    }
  }
  return null;
};

/**
 * Infer a human label from backend slot timing.
 * Rules:
 * 1) If EXACT match to preset -> use that label/type
 * 2) Else -> Custom
 */
export const enrichBackendSlot = (slot: {
  id: string;
  start: string;
  end: string;
}) => {
  const preset = SLOT_PRESETS.find(
    (p) => p.start === slot.start && p.end === slot.end,
  );
  if (preset) {
    return {
      ...slot,
      slotType: preset.slotType as SlotType,
      label: preset.label,
    };
  }

  return {
    ...slot,
    slotType: "custom" as SlotType,
    label: "Custom",
  };
};
